import React, { useCallback, useEffect, useState } from 'react';
import { clsx } from 'clsx';
import { Activity, KeyRound, Loader2, ShieldCheck, Target, UserCheck, UserX, Users } from 'lucide-react';
import { Button } from '@components/Button';
import { ApiErrorModal } from '@components/ApiErrorModal';
import { ProviderIcon } from '@components/ProviderIcon';
import { Switch } from '@components/Switch';
import { useProfile } from '@contexts/ProfileContext';
import profileService from '@services/profileService';
import { PLAN_LABELS, type AppUser } from '@/types/planning';
import { formatDateBR, formatDateTimeBR } from '@/utils/formatters';
import { UserAccessModal } from './UserAccessModal';
import { UserActivityDrawer } from './UserActivityDrawer';

const fmtInt = (n?: number | null) => (n ?? 0).toLocaleString('pt-BR');

/**
 * Administração → Usuários: quem já entrou na aplicação (AppUser) — provedor (Entra ID ou Google),
 * papel, situação do acesso (ativo / teste até / expirado / desativado), plano, último acesso e
 * contadores dos últimos 30 dias; ativar/desativar, "Acesso" (plano e data) e "Atividade" (sessões e
 * uso). O admin NÃO vê dados de outros usuários — só o cadastro.
 */
export const UsersAdminView: React.FC = () => {
  const { profile } = useProfile();
  const [users, setUsers] = useState<AppUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [cacBusyId, setCacBusyId] = useState<number | null>(null);
  const [accessUser, setAccessUser] = useState<AppUser | null>(null);
  const [activityUser, setActivityUser] = useState<AppUser | null>(null);
  const [error, setError] = useState<unknown>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await profileService.listUsers());
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const mergeUser = (updated: AppUser) =>
    setUsers((prev) => prev.map((x) => (x.user_id === updated.user_id ? { ...x, ...updated } : x)));

  const toggle = async (u: AppUser) => {
    setBusyId(u.user_id);
    try {
      mergeUser(await profileService.setUserActive(u.user_id, !u.is_active));
    } catch (err) {
      setError(err);
    } finally {
      setBusyId(null);
    }
  };

  const toggleCac = async (u: AppUser) => {
    setCacBusyId(u.user_id);
    try {
      mergeUser(await profileService.setUserCac(u.user_id, !u.cac_enabled));
    } catch (err) {
      setError(err);
    } finally {
      setCacBusyId(null);
    }
  };

  const onError = useCallback((err: unknown) => setError(err), []);

  /** Interruptor individual do Acervo CAC + badge do efeito (admin: sempre liberado, sem interruptor). */
  const cacControl = (u: AppUser) => {
    if (u.role === 'admin') {
      return (
        <span className="badge-success inline-flex items-center gap-1" title="O administrador sempre tem o Acervo CAC">
          <Target size={11} /> sempre liberado
        </span>
      );
    }
    const badge = u.cac_effective ? (
      <span className="badge-success">{u.cac_enabled ? 'liberado' : 'liberado (geral)'}</span>
    ) : (
      <span className="badge-neutral">não liberado</span>
    );
    return (
      <div className="flex flex-wrap items-center gap-2">
        <Switch
          checked={!!u.cac_enabled}
          onChange={() => toggleCac(u)}
          loading={cacBusyId === u.user_id}
          disabled={cacBusyId !== null || !u.is_active}
          ariaLabel={`Liberar Acervo CAC para ${u.name}`}
        />
        {badge}
      </div>
    );
  };

  const roleBadge = (u: AppUser) =>
    u.role === 'admin' ? (
      <span className="badge-info inline-flex items-center gap-1">
        <ShieldCheck size={11} /> Administrador
      </span>
    ) : (
      <span className="badge-neutral">Usuário</span>
    );

  /** Situação do acesso: Desativado (is_active = false), Expirado, Teste até dd/MM/aaaa ou Ativo. */
  const statusBadge = (u: AppUser) => {
    const status = u.access_status ?? (u.is_active ? 'active' : 'blocked');
    if (status === 'blocked' || !u.is_active) return <span className="badge-neutral">Desativado</span>;
    if (status === 'expired') {
      return (
        <span className="badge-danger" title={u.access_until ? `Acesso até ${formatDateBR(u.access_until)}` : undefined}>
          Expirado
        </span>
      );
    }
    if (u.plan === 'trial' && u.access_until) return <span className="badge-warning whitespace-nowrap">Teste até {formatDateBR(u.access_until)}</span>;
    return (
      <span className="badge-success" title={u.access_until ? `Acesso até ${formatDateBR(u.access_until)}` : 'Sem data de término'}>
        Ativo
      </span>
    );
  };

  const planLabel = (u: AppUser) => {
    const label = u.plan ? PLAN_LABELS[u.plan] ?? u.plan : '—';
    return u.access_until && u.plan !== 'trial' ? `${label} · até ${formatDateBR(u.access_until)}` : label;
  };

  const lastSeen = (u: AppUser) => u.last_seen_at || u.last_login_at;

  const isSelf = (u: AppUser) => u.user_id === profile?.user_id;

  const toggleButton = (u: AppUser) => (
    <Button
      variant="outline"
      size="sm"
      icon={u.is_active ? <UserX /> : <UserCheck />}
      onClick={() => toggle(u)}
      loading={busyId === u.user_id}
      disabled={busyId !== null || isSelf(u)}
      className={clsx(u.is_active ? 'text-red-600 border-red-200 hover:bg-red-50' : 'text-emerald-700 border-emerald-200 hover:bg-emerald-50')}
      title={isSelf(u) ? 'Você não pode desativar o próprio acesso' : undefined}
    >
      {u.is_active ? 'Desativar' : 'Ativar'}
    </Button>
  );

  const accessButton = (u: AppUser) => (
    <Button variant="outline" size="sm" icon={<KeyRound />} onClick={() => setAccessUser(u)} disabled={busyId !== null} title="Plano e data de término do acesso">
      Acesso
    </Button>
  );

  const activityButton = (u: AppUser) => (
    <Button variant="outline" size="sm" icon={<Activity />} onClick={() => setActivityUser(u)} title="Sessões e uso por módulo">
      Atividade
    </Button>
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-h3 flex items-center gap-2">
          <Users size={20} className="text-slate-500" /> Usuários
        </h1>
      </div>
      <p className="text-small">
        Lista de quem já entrou no Plan. O acesso vem do <strong>Entra ID</strong> (conta Microsoft atribuída ao aplicativo) ou do{' '}
        <strong>cadastro com Google</strong> (criado no primeiro login, com período de teste — ver{' '}
        <a href="/admin/settings" className="text-emerald-700 hover:underline">
          Configurações
        </a>
        ). Aqui é possível desativar um acesso, definir plano e data de término (<strong>Acesso</strong>), ver sessões e uso por módulo
        (<strong>Atividade</strong>) e liberar o <strong>Acervo CAC</strong> por usuário. Cada usuário vê apenas os próprios dados — inclusive o
        administrador, que aqui enxerga só cadastro, sessões e contadores.
      </p>

      {/* Celular: cartões */}
      <div className="md:hidden space-y-2">
        {loading && users.length === 0 && (
          <p className="text-center text-slate-400 py-6">
            <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
          </p>
        )}
        {!loading && users.length === 0 && <p className="text-center text-slate-500 py-6">Nenhum usuário entrou ainda.</p>}
        {users.map((u) => (
          <div key={u.user_id} className={clsx('card p-3 space-y-2', !u.is_active && 'opacity-60')}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium text-slate-800 flex items-center gap-1.5 min-w-0">
                  <span className="truncate">{u.name}</span>
                  <ProviderIcon provider={u.provider} size={12} />
                </p>
                <p className="text-small [overflow-wrap:anywhere]">{u.email}</p>
              </div>
              {roleBadge(u)}
            </div>
            <div className="flex flex-wrap items-center gap-2 text-small">
              {statusBadge(u)}
              <span className="badge-neutral">{planLabel(u)}</span>
            </div>
            <div className="text-small space-y-0.5">
              <p>{lastSeen(u) ? `Último acesso ${formatDateTimeBR(lastSeen(u))}` : 'Nunca acessou'}</p>
              <p>
                30 dias: {fmtInt(u.sessions_30d)} sessões · {fmtInt(u.requests_30d)} requisições
              </p>
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2 text-small">
              <span className="inline-flex items-center gap-1">
                <Target size={12} /> Acervo CAC
              </span>
              {cacControl(u)}
            </div>
            <div className="flex flex-wrap gap-2">
              {toggleButton(u)}
              {accessButton(u)}
              {activityButton(u)}
            </div>
          </div>
        ))}
      </div>

      {/* Tabela */}
      <div className="table-container bg-white hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="table-header">
              <tr>
                <th>Usuário</th>
                <th>Provedor</th>
                <th>Papel</th>
                <th>Situação</th>
                <th>Plano</th>
                <th>Último acesso</th>
                <th className="text-right" title="Sessões nos últimos 30 dias">
                  Sessões 30 d
                </th>
                <th className="text-right" title="Requisições nos últimos 30 dias">
                  Req. 30 d
                </th>
                <th>Acervo CAC</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {loading && users.length === 0 && (
                <tr>
                  <td colSpan={10} className="table-cell text-center text-slate-400">
                    <Loader2 size={18} className="inline animate-spin mr-2" /> Carregando…
                  </td>
                </tr>
              )}
              {!loading && users.length === 0 && (
                <tr>
                  <td colSpan={10} className="table-cell text-center text-slate-500 py-8">
                    Nenhum usuário entrou ainda.
                  </td>
                </tr>
              )}
              {users.map((u) => (
                <tr key={u.user_id} className={clsx('table-row', !u.is_active && 'opacity-60')}>
                  <td className="table-cell">
                    <p className="font-medium text-slate-800">{u.name}</p>
                    <p className="text-small">{u.email}</p>
                  </td>
                  <td className="table-cell whitespace-nowrap">
                    <ProviderIcon provider={u.provider} size={14} withLabel />
                  </td>
                  <td className="table-cell">{roleBadge(u)}</td>
                  <td className="table-cell">{statusBadge(u)}</td>
                  <td className="table-cell whitespace-nowrap">{planLabel(u)}</td>
                  <td className="table-cell whitespace-nowrap">
                    {lastSeen(u) ? formatDateTimeBR(lastSeen(u)) : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="table-cell text-right tabular-nums">{fmtInt(u.sessions_30d)}</td>
                  <td className="table-cell text-right tabular-nums">{fmtInt(u.requests_30d)}</td>
                  <td className="table-cell">{cacControl(u)}</td>
                  <td className="table-cell">
                    <div className="flex flex-wrap justify-end gap-2">
                      {toggleButton(u)}
                      {accessButton(u)}
                      {activityButton(u)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {accessUser && (
        <UserAccessModal
          user={accessUser}
          onClose={() => setAccessUser(null)}
          onSaved={(updated) => {
            mergeUser(updated);
            setAccessUser(null);
          }}
          onError={onError}
        />
      )}

      {activityUser && <UserActivityDrawer user={activityUser} onClose={() => setActivityUser(null)} onError={onError} />}

      <ApiErrorModal error={error} onClose={() => setError(null)} />
    </div>
  );
};
